import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, Loader2, ChevronLeft, ChevronRight, Eye,
  ArrowUpDown, ArrowUp, ArrowDown, X,
} from 'lucide-react';
import { rulesApi } from '../../services/api';
import type { CatalogRule } from '../../types';
import RuleDetailModal from './RuleDetailModal';
import RequestRuleModal from './RequestRuleModal';

type SortCol = 'rule' | 'owner' | 'category' | 'status' | 'modified_date';
type SortDir = 'asc' | 'desc';
type SearchBy = 'search' | 'tag' | 'be_support';
type Category = '' | 'OOB' | 'Custom';
type Status = '' | 'Production' | 'Beta' | 'Experimental' | 'Requested';

const STATUS_COLORS: Record<string, string> = {
  production:   'bg-green-500/20 text-green-400 border-green-500/30',
  beta:         'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  experimental: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  requested:    'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const SEARCH_BY_LABELS: Record<SearchBy, string> = {
  search: 'Name',
  tag: 'Tag',
  be_support: 'Backend',
};

function statusBadge(status: string) {
  const cls = STATUS_COLORS[status.toLowerCase()] || 'bg-cargo-panel text-cargo-muted border-cargo-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {status}
    </span>
  );
}

const RuleCatalog: React.FC = () => {
  const [rules, setRules] = useState<CatalogRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters & pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchBy, setSearchBy] = useState<SearchBy>('search');
  const [category, setCategory] = useState<Category>('');
  const [status, setStatus] = useState<Status>('');
  const [sortCol, setSortCol] = useState<SortCol>('modified_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Modals
  const [detailRule, setDetailRule] = useState<CatalogRule | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);

  // Expanded rows
  const [expandedDesc, setExpandedDesc] = useState<number | null>(null);
  const [expandedTags, setExpandedTags] = useState<number | null>(null);
  const [expandedBe, setExpandedBe] = useState<number | null>(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        items_per_page: perPage,
        sort_col: sortCol,
        sort_dir: sortDir,
      };
      if (searchText.trim()) {
        params[searchBy] = searchText.trim();
      }
      if (category) params.category = category;
      if (status) params.status = status;

      const resp = await rulesApi.list(params);
      const payload = resp.data.payload;
      const rulesList = payload?.rules || payload || [];
      setRules(Array.isArray(rulesList) ? rulesList : []);
      const pagination = payload?.pagination;
      if (pagination) {
        setTotalCount(pagination.count || 0);
        setTotalPages(pagination.pages || Math.ceil((pagination.count || 0) / perPage) || 1);
      } else {
        setTotalCount(Array.isArray(rulesList) ? rulesList.length : 0);
        setTotalPages(1);
      }
    } catch {
      setRules([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchText, searchBy, category, status, sortCol, sortDir]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    setPage(1);
  };

  const handlePerPageChange = (val: number) => {
    setPerPage(val);
    setPage(1);
  };

  const SortIcon = ({ col }: { col: SortCol }) => {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cargo-text">Rule Catalog</h1>
          <p className="text-sm text-cargo-muted mt-1">
            {totalCount} rule{totalCount !== 1 ? 's' : ''} available
          </p>
        </div>
        <button
          onClick={() => setRequestOpen(true)}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Request New Rule
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search with search-by dropdown */}
          <div className="relative flex flex-1 min-w-[280px]">
            <select
              value={searchBy}
              onChange={(e) => { setSearchBy(e.target.value as SearchBy); setPage(1); }}
              className="px-3 py-2.5 rounded-l-lg bg-cargo-panel border border-r-0 border-cargo-border
                         text-cargo-muted text-sm outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
            >
              {(Object.keys(SEARCH_BY_LABELS) as SearchBy[]).map((k) => (
                <option key={k} value={k}>{SEARCH_BY_LABELS[k]}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
              <input
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={`Search by ${SEARCH_BY_LABELS[searchBy].toLowerCase()}...`}
                className="w-full pl-10 pr-8 py-2.5 rounded-r-lg bg-cargo-panel border border-cargo-border
                           text-cargo-text placeholder-cargo-muted/50 text-sm
                           focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
              />
              {searchText && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-cargo-muted hover:text-cargo-text"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category filter */}
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value as Category); setPage(1); }}
            className="pl-3 pr-8 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                       text-cargo-text text-sm outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
          >
            <option value="">All Categories</option>
            <option value="OOB">OOB</option>
            <option value="Custom">Custom</option>
          </select>

          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as Status); setPage(1); }}
            className="pl-3 pr-8 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                       text-cargo-text text-sm outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="Production">Production</option>
            <option value="Beta">Beta</option>
            <option value="Experimental">Experimental</option>
            <option value="Requested">Requested</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cargo-border">
                {([
                  ['rule', 'Rule Name', true],
                  ['owner', 'Owner', true],
                  ['category', 'Category', true],
                  ['', 'Tags', false],
                  ['', 'Backend', false],
                  ['status', 'Status', true],
                  ['', '', false],
                ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                    onClick={sortable ? () => handleSort(col as SortCol) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortable && col && <SortIcon col={col as SortCol} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                    <p className="text-cargo-muted text-sm mt-2">Loading rules...</p>
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-cargo-muted text-sm">
                    No rules found.
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr
                    key={rule.id}
                    className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors"
                  >
                    {/* Rule Name + Description */}
                    <td className="px-4 py-3 max-w-[320px]">
                      <p className="text-cargo-text font-mono text-sm truncate">{rule.rule}</p>
                      <p
                        className={`text-xs text-cargo-muted mt-0.5 ${
                          expandedDesc === rule.id ? '' : 'truncate'
                        } cursor-pointer`}
                        onClick={() => setExpandedDesc(expandedDesc === rule.id ? null : rule.id)}
                        title={rule.description}
                      >
                        {rule.description}
                      </p>
                    </td>

                    {/* Owner */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{rule.owner}</td>

                    {/* Category */}
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted">
                        {rule.category}
                      </span>
                    </td>

                    {/* Tags */}
                    <td className="px-4 py-3">
                      {rule.tags.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent">
                            {rule.tags[0]}
                          </span>
                          {rule.tags.length > 1 && (
                            expandedTags === rule.id ? (
                              <>
                                {rule.tags.slice(1).map((t) => (
                                  <span key={t} className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent">
                                    {t}
                                  </span>
                                ))}
                                <button
                                  onClick={() => setExpandedTags(null)}
                                  className="text-xs text-cargo-muted hover:text-cargo-text"
                                >
                                  less
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setExpandedTags(rule.id)}
                                className="px-1.5 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted hover:text-cargo-text"
                              >
                                +{rule.tags.length - 1}
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-cargo-muted/40 text-xs">—</span>
                      )}
                    </td>

                    {/* Supported Backend */}
                    <td className="px-4 py-3">
                      {rule.supported_backend.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="px-2 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted">
                            {rule.supported_backend[0]}
                          </span>
                          {rule.supported_backend.length > 1 && (
                            expandedBe === rule.id ? (
                              <>
                                {rule.supported_backend.slice(1).map((b) => (
                                  <span key={b} className="px-2 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted">
                                    {b}
                                  </span>
                                ))}
                                <button
                                  onClick={() => setExpandedBe(null)}
                                  className="text-xs text-cargo-muted hover:text-cargo-text"
                                >
                                  less
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setExpandedBe(rule.id)}
                                className="px-1.5 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted hover:text-cargo-text"
                              >
                                +{rule.supported_backend.length - 1}
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-cargo-muted/40 text-xs">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">{statusBadge(rule.status)}</td>

                    {/* View button */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setDetailRule(rule)}
                        className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && rules.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-cargo-border">
            <div className="flex items-center gap-2 text-sm text-cargo-muted">
              <span>Rows per page:</span>
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(Number(e.target.value))}
                className="pl-2 pr-7 py-1 rounded bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                           outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-cargo-muted">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                             disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                             disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {detailRule && (
        <RuleDetailModal rule={detailRule} onClose={() => setDetailRule(null)} />
      )}
      {requestOpen && (
        <RequestRuleModal
          onClose={() => setRequestOpen(false)}
          onSuccess={() => { setRequestOpen(false); fetchRules(); }}
        />
      )}
    </div>
  );
};

export default RuleCatalog;
